import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft, Edit3, TrendingUp, Box, Tag, Layers, Plus,
  MapPin, Settings, Trash2, RefreshCcw,
  Barcode, FlaskConical, Check, X, Info, Package, DollarSign,
  AlignLeft, Award, Lock, AlertTriangle
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import VariantesMatrizEditor from '../components/productos/VariantesMatrizEditor';
import { isVariantTracked } from '../utils/variantStock';
import { buildProductStatusPatch, normalizeProductStatus } from '../utils/productStatus';
import {
  getProductDeletionPolicy,
  loadProductDeletionImpact,
  deleteProduct,
} from '../utils/productDeletion';
import { useReadOnly } from '../hooks/useReadOnly';

/**
 * ProductoDetalle - Ficha de producto
 * - Layout alineado a Productos (compacto, sin espacios excesivos)
 * - Variantes: solo visibles/operables si organizations.enable_variants = true
 * - Si hay 1 sola, no se presenta como “Variantes” (se muestra como “Opciones del producto”)
 * - Precio / costo / margen objetivo: se gestionan a nivel producto y se aplican a TODAS las variantes
 * - Sin modales: confirmaciones embebidas
 * * UPDATE: Corrección de cálculo de costo unitario basado en receta (división por rendimiento).
 */

const GREEN = '#3FF2AA';
const GREEN_TEXT = '#0B2A36';
const BLUE = '#3B82F6';
const COST_ONLY_PRODUCT_TYPES = new Set(['Materia Prima', 'Insumo', 'Insumo / Materia Prima']);
const RED = '#F05A57';

// --- FIX IMÁGENES: Función ROBUSTA para reconstruir la URL ---
const resolveImageUrl = (path) => {
    if (!path) return null;
    const rawPath = path.trim();
    if (rawPath.startsWith('blob:')) return null; // Ignorar URLs temporales rotas
    if (rawPath.includes('http') || rawPath.includes('base64')) return rawPath;
    if (rawPath.startsWith('/img/') || rawPath.startsWith('img/')) {
      return rawPath.startsWith('/') ? rawPath : `/${rawPath}`;
    }
    if (rawPath.startsWith('/storage/')) {
      return `https://ltpbzblgasmfvcagtlti.supabase.co${rawPath}`;
    }

    const PROJECT_URL = "https://ltpbzblgasmfvcagtlti.supabase.co";
    const BUCKET_NAME = "products";
    const cleanPath = rawPath.replace(/^\/+/, '');
    return `${PROJECT_URL}/storage/v1/object/public/${BUCKET_NAME}/${cleanPath}`;
};

const ProductoDetalle = ({ onNavigate }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { organization, refreshOrganization } = useAuth();

  const [producto, setProducto] = useState(null);
  const [receta, setReceta] = useState(null);
  const [loading, setLoading] = useState(true);

  const [isConfirmingDeactivate, setIsConfirmingDeactivate] = useState(false);

  const [orgEnableVariants, setOrgEnableVariants] = useState(false);
  const [allowZeroSellPrice, setAllowZeroSellPrice] = useState(false);
  const [variantsBusy, setVariantsBusy] = useState(false);

  // Eliminación de producto (lógicos en Configuración → Mi empresa → Reglas).
  const { guardAction } = useReadOnly();
  const deletionPolicy = useMemo(() => getProductDeletionPolicy(organization), [organization]);
  const [deleteImpact, setDeleteImpact] = useState(null); // null = panel cerrado
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');

  // “Shared economics” (aplica a todas las variantes)
  const [sharedPrice, setSharedPrice] = useState(0);
  const [sharedCost, setSharedCost] = useState(0);
  const [targetMargin, setTargetMargin] = useState(30);

  // IVA % y precio en USD (si price_usd > 0, el precio en ARS se calcula con la cotización de la empresa)
  // Nota: la cotización se guarda en organizations.usd_exchange_rate (se agrega en EmpresaDatos).
  const [ivaPercent, setIvaPercent] = useState(21);
  const [priceUsd, setPriceUsd] = useState(0);
  const [usdRate, setUsdRate] = useState(null); // ARS por 1 USD (ej: 1200). null = no disponible / no cargada

  // Draft para alta de opción/variante (solo metadata)
  const [variantDraft, setVariantDraft] = useState({
    variant_name: '',
    sku: '',
    barcode: '',
    variant_description: ''
  });

  const [showAddVariant, setShowAddVariant] = useState(false);

  // ---------------- Navegación “a prueba de balas” ----------------
  const go = (module, entityId = null) => {
    try {
      if (typeof onNavigate === 'function') return onNavigate(module, entityId);
      if (entityId) return navigate(`/sistema/${module}/${entityId}`);
      return navigate(`/sistema/${module}`);
    } catch (e) {
      console.error('Error navegando:', e);
      toast.error('No se pudo navegar. Revisá rutas en Sistema.jsx');
    }
  };

  useEffect(() => {
    if (id) fetchDatosCompletos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const safeObj = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {});
  const getAttr = (v, key, fallback = '') => {
    const a = safeObj(v?.attributes);
    const val = a?.[key];
    return typeof val === 'string' ? val : (val == null ? fallback : String(val));
  };

  const buildVariantAttributes = (variant, patch = {}) => {
    const a = safeObj(variant?.attributes);
    return { ...a, ...patch };
  };

  const fetchDatosCompletos = async () => {
    try {
      setLoading(true);

      // NOTA: 'select(*)' ya traerá 'brand' y 'description' si existen en la tabla
      const { data: prodData, error: prodError } = await supabase
        .from('products')
        .select(`
          *,
          product_categories(name),
          units_of_measure(name, abbreviation),
          product_variants(id, sku, price, cost_price, barcode, attributes, created_at),
          stock_levels(*, sitios(nombre))
        `)
        .eq('id', id)
        .single();

      if (prodError) throw prodError;

      const { data: recetaData } = await supabase
        .from('recetas')
        .select('*')
        .eq('producto_id', id)
        .maybeSingle();

      // Feature flag: Variantes (organizations.enable_variants) — viene del AuthContext.
      let enableVariants = false;
      const orgRow = organization;
      if (orgRow) {
        enableVariants = Boolean(orgRow?.enable_variants);
        setAllowZeroSellPrice(Boolean(orgRow?.allow_zero_sell_price));
        const rate = Number(orgRow?.usd_exchange_rate);
        if (Number.isFinite(rate) && rate > 0) setUsdRate(rate);
      }

      // Garantía mínima: si el producto no tiene variantes, crear 1 (silencioso)
      // (aunque el flag esté apagado, se mantiene la consistencia del modelo)
      try {
        const pv = Array.isArray(prodData?.product_variants) ? prodData.product_variants : [];
        if (pv.length === 0 && prodData?.organization_id) {
          await supabase.from('product_variants').insert([{
            product_id: prodData.id,
            organization_id: prodData.organization_id,
            sku: 'DEFAULT',
            price: 0,
            cost_price: Number(prodData.cost_price) || 0,
            barcode: prodData.barcode || null,
            attributes: { variant_name: '', variant_description: '' }
          }]);
          const { data: prodRe } = await supabase
            .from('products')
            .select('*, product_variants(id, sku, price, cost_price, barcode, attributes, created_at)')
            .eq('id', id)
            .single();
          if (prodRe) prodData.product_variants = prodRe.product_variants || [];
        }
      } catch (e) {
        console.warn('No se pudo garantizar variante default:', e);
      }

      setProducto(normalizeProductStatus(prodData));
      setReceta(recetaData || null);
      setOrgEnableVariants(enableVariants);

      // Inicializar shared economics
      const variants = Array.isArray(prodData?.product_variants) ? prodData.product_variants : [];
      const first = variants[0] || {};
      
      setSharedPrice(Number(prodData.sell_price || first?.price || 0));
      
      // --- CÁLCULO DE COSTO UNITARIO REAL ---
      let finalUnitCost = 0;

      // 1. Si existe receta, calculamos dinámicamente: Total Ingredientes / Rendimiento
      if (recetaData && Array.isArray(recetaData.ingredientes) && recetaData.ingredientes.length > 0) {
          const totalIngredientes = recetaData.ingredientes.reduce((acc, i) => acc + (Number(i.costo_unitario||0) * Number(i.quantity||0)), 0);
          const rendimiento = Number(recetaData.rendimiento || 1);
          // Dividimos por rendimiento para tener costo unitario
          finalUnitCost = rendimiento > 0 ? (totalIngredientes / rendimiento) : totalIngredientes;
      } else {
          // 2. Si no hay receta, usamos el costo guardado en la tabla products
          finalUnitCost = prodData.cost_price !== null 
            ? Number(prodData.cost_price) 
            : (Number(first?.cost_price) || 0);
      }
      
      // Asignamos el valor calculado redondeado a 2 decimales
      setSharedCost(finalUnitCost.toFixed(2));
      
      setTargetMargin(Number(prodData?.target_margin) || 30);

      // Nuevos campos
      setIvaPercent(Number(prodData?.iva_percentage ?? 21) || 0);
      setPriceUsd(Number(prodData?.price_usd || 0) || 0);
    } catch (error) {
      console.error(error);
      toast.error('Error al cargar la ficha');
      go('productos');
    } finally {
      setLoading(false);
    }
  };

  const refreshVariants = async () => {
    const { data } = await supabase
      .from('product_variants')
      .select('id, sku, price, cost_price, barcode, attributes, created_at')
      .eq('product_id', id)
      .order('created_at', { ascending: true });
    setProducto((prev) => (prev ? ({ ...prev, product_variants: data || [] }) : prev));
  };

  const applySharedToAllVariants = async (patch) => {
    setVariantsBusy(true);
    try {
      const { error } = await supabase
        .from('product_variants')
        .update(patch)
        .eq('product_id', id);
      if (error) throw error;
      await refreshVariants();
    } catch (e) {
      console.error(e);
      toast.error('No se pudieron actualizar las opciones');
    } finally {
      setVariantsBusy(false);
    }
  };

  // --- Handlers para actualización en tiempo real visual (UX) ---
  const handleCostChange = (e) => {
    const val = e.target.value;
    setSharedCost(val);
    
    // Si la actualización automática está activa, calculamos visualmente al instante
    if (producto?.auto_update_price) {
      const c = Number(val) || 0;
      const m = Number(targetMargin) || 0;
      if (c >= 0) {
        const p = c * (1 + (m / 100));
        setSharedPrice(p);
      }
    }
  };

  const handleMarginChange = (e) => {
    const val = e.target.value;
    setTargetMargin(val);

    if (producto?.auto_update_price) {
      const c = Number(sharedCost) || 0;
      const m = Number(val) || 0;
      if (c >= 0) {
        const p = c * (1 + (m / 100));
        setSharedPrice(p);
      }
    }
  };

  const saveSharedEconomics = async (next) => {
    if (!producto) return;
    
    let nextPrice = Number(next?.price ?? sharedPrice) || 0;
    const nextCostRaw = Number(next?.cost ?? sharedCost) || 0;
    const nextTarget = Number(next?.target_margin ?? targetMargin) || 0;

    // Asegurar redondeo a 2 decimales para la base de datos (Garantiza integridad para Gestión de Costos)
    const nextCost = Number(nextCostRaw.toFixed(2));

    // --- Lógica de Seguridad de Actualización Automática ---
    if (producto.auto_update_price && next?.price === undefined) {
      if (next?.cost !== undefined || next?.target_margin !== undefined) {
        if (nextCost >= 0) {
           nextPrice = Number((nextCost * (1 + (nextTarget / 100))).toFixed(2));
        }
      }
    }

    if (!allowZeroSellPrice && !COST_ONLY_PRODUCT_TYPES.has(String(producto.product_type || '').trim()) && nextPrice <= 0) {
      toast.error('Definí un precio final mayor a 0 o activá la regla para permitir $0');
      return;
    }

    try {
      // 1. UPDATE TABLA PRODUCTS (Registro Maestro)
      // Actualizamos el campo 'cost_price' en la tabla products para que quede registrado correctamente
      const { error } = await supabase
        .from('products')
        .update({ 
          cost_price: nextCost, 
          target_margin: nextTarget,
          sell_price: nextPrice 
        })
        .eq('id', id);
      if (error) throw error;
    } catch (e) {
      console.error(e);
      toast.error('No se pudo guardar la configuración');
      return;
    }

    // 2. Variantes (sincronizar para mantener consistencia)
    await applySharedToAllVariants({ price: nextPrice, cost_price: nextCost });

    // 3. State local (formateado)
    setSharedPrice(nextPrice);
    setSharedCost(nextCost.toFixed(2));
    setTargetMargin(nextTarget);

    // 4. Producto local
    setProducto((prev) => prev ? ({ 
      ...prev, 
      cost_price: nextCost, 
      target_margin: nextTarget,
      sell_price: nextPrice 
    }) : prev);

    toast.success('Costo y precios actualizados');
  };

  const handleToggleAutoPrice = async () => {
    if (!producto) return;
    const next = !producto.auto_update_price;
    try {
      const { error } = await supabase
        .from('products')
        .update({ auto_update_price: next })
        .eq('id', id);
      if (error) throw error;
      setProducto((p) => (p ? ({ ...p, auto_update_price: next }) : p));
      
      toast.success(next ? 'Actualización automática activada' : 'Actualización automática desactivada');
    } catch (e) {
      console.error(e);
      toast.error('No se pudo actualizar');
    }
  };

  // Guarda campos extra del producto (IVA% y precio USD)
  const saveProductExtras = async (patch) => {
    if (!producto) return;
    try {
      const { error } = await supabase.from('products').update(patch).eq('id', id);
      if (error) throw error;
      setProducto((p) => (p ? ({ ...p, ...patch }) : p));
    } catch (e) {
      console.error(e);
      toast.error('No se pudo guardar');
    }
  };

  const handleSaveIva = async () => {
    const iva = Number(ivaPercent);
    if (!Number.isFinite(iva) || iva < 0 || iva > 100) {
      toast.error('IVA inválido');
      return;
    }
    await saveProductExtras({ iva_percentage: iva });
    toast.success('IVA actualizado');
  };

  const handleSaveUsdPrice = async () => {
    const usd = Number(priceUsd || 0);
    if (!Number.isFinite(usd) || usd < 0) {
      toast.error('Precio USD inválido');
      return;
    }

    // 1) Guardar en producto
    await saveProductExtras({ price_usd: usd });

    // 2) Si hay cotización, aplicar precio ARS a variantes para mantener consistencia operativa
    if (usd > 0 && usdArsPrice != null) {
      await applySharedToAllVariants({ price: Number(usdArsPrice) || 0 });
      setSharedPrice(Number(usdArsPrice) || 0);
      toast.success('Precio USD aplicado');
      return;
    }

    toast.success('Precio USD actualizado');
  };

  const handleDeactivate = async () => {
    if (!producto) return;
    const newStatus = !normalizeProductStatus(producto)?.active;

    try {
      const { error } = await supabase
        .from('products')
        .update(buildProductStatusPatch(newStatus))
        .eq('id', id);

      if (error) throw error;

      toast.success(newStatus ? 'Producto activado' : 'Producto desactivado');
      go('productos');
    } catch (error) {
      console.error(error);
      toast.error('Error en la operación');
    } finally {
      setIsConfirmingDeactivate(false);
    }
  };

  // ---------------- Variantes (solo metadata) ----------------
  const addVariant = async () => {
    if (!orgEnableVariants) return;
    if (!producto?.organization_id) return;

    setVariantsBusy(true);
    try {
      const payload = {
        product_id: producto.id,
        organization_id: producto.organization_id,
        sku: (variantDraft.sku || '').trim() || null,
        price: Number(sharedPrice) || 0,
        cost_price: Number(sharedCost) || 0,
        barcode: (variantDraft.barcode || '').trim() || null,
        attributes: {
          variant_name: String(variantDraft.variant_name || ''),
          variant_description: String(variantDraft.variant_description || '')
        }
      };

      const { error } = await supabase.from('product_variants').insert([payload]);
      if (error) throw error;

      setVariantDraft({ variant_name: '', sku: '', barcode: '', variant_description: '' });
      await refreshVariants();
      toast.success('Opción agregada');
    } catch (e) {
      console.error(e);
      toast.error('No se pudo agregar');
    } finally {
      setVariantsBusy(false);
    }
  };

  const updateVariantMeta = async (variantId, patch) => {
    if (!orgEnableVariants) return;
    setVariantsBusy(true);
    try {
      const { error } = await supabase.from('product_variants').update(patch).eq('id', variantId);
      if (error) throw error;
      await refreshVariants();
    } catch (e) {
      console.error(e);
      toast.error('No se pudo actualizar');
    } finally {
      setVariantsBusy(false);
    }
  };

  const deleteVariant = async (variantId) => {
    if (!orgEnableVariants) return;

    const pv = Array.isArray(producto?.product_variants) ? producto.product_variants : [];
    if (pv.length <= 1) {
      toast.error('El producto debe tener al menos 1 opción.');
      return;
    }

    setVariantsBusy(true);
    try {
      const { error } = await supabase.from('product_variants').delete().eq('id', variantId);
      if (error) throw error;
      await refreshVariants();
      toast.success('Opción eliminada');
    } catch (e) {
      console.error(e);
      toast.error('No se pudo eliminar');
    } finally {
      setVariantsBusy(false);
    }
  };

  // --- Eliminación de producto ---------------------------------------------
  // El panel se abre recién con el impacto en la mano: nunca se ofrece borrar
  // sin poder decir qué se lleva puesto.
  const abrirPanelEliminar = guardAction(async () => {
    if (!producto?.id || !organization?.id) return;
    setDeleteBusy(true);
    try {
      const impacto = await loadProductDeletionImpact({
        supabase,
        productId: producto.id,
        organizationId: organization.id,
      });
      setDeleteConfirmName('');
      setDeleteImpact(impacto);
    } catch (e) {
      console.error('No se pudo evaluar el producto', e);
      toast.error('No se pudo evaluar el producto');
    } finally {
      setDeleteBusy(false);
    }
  });

  const confirmarEliminar = guardAction(async () => {
    if (!deleteImpact || !producto?.id || !organization?.id) return;
    setDeleteBusy(true);
    try {
      // Sin pasarle el impacto: deleteProduct lo recalcula contra la base para
      // no operar sobre un snapshot viejo (puede haber habido una venta desde
      // que se abrió el panel).
      const { mode, adjustments } = await deleteProduct({
        supabase,
        product: producto,
        organization,
        organizationId: organization.id,
      });

      toast.success(
        mode === 'hard'
          ? 'Producto eliminado'
          : adjustments > 0
          ? `Producto eliminado. Stock ajustado a 0 en ${adjustments} ${adjustments === 1 ? 'sucursal' : 'sucursales'}.`
          : 'Producto eliminado',
      );
      setDeleteImpact(null);
      go('productos');
    } catch (e) {
      console.error('No se pudo eliminar el producto', e);
      if (e?.code === 'HAS_HISTORY') {
        toast.error('El producto tiene movimientos y esa opción no está habilitada');
      } else if (e?.code === 'DELETION_DISABLED') {
        toast.error('La eliminación de productos no está habilitada');
      } else if (e?.code === '23503') {
        // Red de contención: alguna FK que no contemplamos sigue apuntando acá.
        toast.error('El producto está referenciado por otros registros y no se puede eliminar');
      } else {
        toast.error('No se pudo eliminar el producto');
      }
    } finally {
      setDeleteBusy(false);
    }
  });

  const fmtMoney = (n) => {
    const v = Number(n || 0);
    return v.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' });
  };

  // Precio ARS calculado por USD * cotización (si corresponde)
  const usdArsPrice = useMemo(() => {
    const usd = Number(priceUsd || 0);
    const rate = Number(usdRate || 0);
    if (usd > 0 && rate > 0) return usd * rate;
    return null;
  }, [priceUsd, usdRate]);

  const isUsdPricing = useMemo(() => Number(priceUsd || 0) > 0, [priceUsd]);

  // Costo base: products.cost_price, o si hay receta, se calcula por ingredientes/rendimiento
  const costoBase = useMemo(() => {
    // Usamos el sharedCost (que viene de la DB o del input, ya calculado) como fuente de verdad visual
    let costo = Number(sharedCost || 0);
    return Number.isFinite(costo) ? costo : 0;
  }, [sharedCost]);

  const precioVenta = useMemo(() => {
    // Si el producto tiene price_usd y hay cotización, el precio ARS queda determinado por eso
    if (usdArsPrice != null) return Number(usdArsPrice) || 0;
    const v = Number(sharedPrice || 0);
    return Number.isFinite(v) ? v : 0;
  }, [sharedPrice, usdArsPrice]);

  const suggestedPrice = useMemo(() => {
    const tgt = Number(targetMargin || 0);
    const base = Number(costoBase || 0);
    if (base <= 0) return 0;
    return base * (1 + (tgt / 100));
  }, [costoBase, targetMargin]);

  const kpi = useMemo(() => {
    const ganancia = Number(precioVenta) - Number(costoBase);
    const margen = Number(costoBase) > 0 ? ((ganancia / Number(costoBase)) * 100) : 0;

    const levels = Array.isArray(producto?.stock_levels) ? producto.stock_levels : [];
    const stockTotal = levels.reduce((acc, l) => acc + (Number(l?.quantity) || 0), 0);

    return {
      ganancia,
      margen: Number.isFinite(margen) ? margen.toFixed(1) : '0.0',
      stockTotal
    };
  }, [precioVenta, costoBase, producto]);

  const variants = Array.isArray(producto?.product_variants) ? producto.product_variants : [];
  const showVariantsBlock = orgEnableVariants && variants.length > 1;
  const showOptionsBlock = orgEnableVariants && variants.length <= 1;

  const uom = producto?.units_of_measure?.abbreviation || '';

  // =========================
  // REALTIME USD RATE SUBSCRIPTION (NEW)
  // =========================
  const usdRateChannelRef = useRef(null);

  useEffect(() => {
    if (!producto?.organization_id) return;

    // Limpieza previa
    try {
      if (usdRateChannelRef.current) {
        supabase.removeChannel(usdRateChannelRef.current);
        usdRateChannelRef.current = null;
      }
    } catch (e) { /* cleanup best-effort */ }

    const channel = supabase
      .channel(`org-usd-rate-${producto.organization_id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'organizations',
          filter: `id=eq.${producto.organization_id}`,
        },
        (payload) => {
          setAllowZeroSellPrice(Boolean(payload?.new?.allow_zero_sell_price));
          const nextRate = Number(payload?.new?.usd_exchange_rate || 0);
          if (!Number.isFinite(nextRate) || nextRate <= 0) return;

          setUsdRate((prev) => {
            const prevNum = Number(prev || 0);
            if (prevNum === nextRate) return prev;
            return nextRate;
          });
        }
      )
      .subscribe((status) => {
        // Optional: console.log('Realtime status', status);
      });

    usdRateChannelRef.current = channel;

    return () => {
      try {
        if (usdRateChannelRef.current) supabase.removeChannel(usdRateChannelRef.current);
      } catch (e) { /* cleanup best-effort */ }
      usdRateChannelRef.current = null;
    };
  }, [producto?.organization_id, priceUsd]);

  // =========================
  // POLLING BACKUP (NEW)
  // =========================
  // Polling: refrescamos la organización vía AuthContext (centraliza el cache).
  useEffect(() => {
    if (!producto?.organization_id) return;
    const interval = setInterval(() => {
      refreshOrganization?.();
    }, 45000);
    return () => clearInterval(interval);
  }, [producto?.organization_id, refreshOrganization]);

  // Sincronizamos usdRate y allowZeroSellPrice cuando cambia la organización.
  useEffect(() => {
    if (!organization) return;
    setAllowZeroSellPrice(Boolean(organization?.allow_zero_sell_price));
    const nextRate = Number(organization?.usd_exchange_rate || 0);
    if (!Number.isFinite(nextRate) || nextRate <= 0) return;
    setUsdRate((prev) => (Number(prev || 0) === nextRate ? prev : nextRate));
  }, [organization?.usd_exchange_rate, organization?.allow_zero_sell_price]);

  // =========================
  // SYNC USD->ARS TO VARIANTS (NEW)
  // =========================
  const lastSyncedUsdRef = useRef({ usd: null, rate: null });

  const usdSyncTimerRef = useRef(null);

  const syncUsdPriceToVariants = async (arsPrice) => {
    if (!producto?.id) return;
    if (!Number.isFinite(Number(arsPrice)) || Number(arsPrice) <= 0) return;

    // Evitar spam de updates si ya coincide con el sharedPrice actual (o muy cerca)
    const current = Number(sharedPrice || 0);
    if (Math.abs(current - Number(arsPrice)) < 0.01) return;

    await applySharedToAllVariants({ price: Number(arsPrice) });
    setSharedPrice(Number(arsPrice));
  };

  useEffect(() => {
    if (loading) return;
    if (!producto?.id) return;

    // Respetar el switch de "Actualización automática de precio"
    if (!producto?.auto_update_price) return;

    if (!isUsdPricing) return;
    if (!usdRate || Number(usdRate) <= 0) return;
    if (usdArsPrice == null) return;

    // Evitar re-sync si no cambió usd o rate
    const usd = Number(priceUsd || 0);
    const rate = Number(usdRate || 0);
    const last = lastSyncedUsdRef.current;
    if (last.usd === usd && last.rate === rate) return;

    // Debounce para evitar múltiples updates seguidos
    if (usdSyncTimerRef.current) clearTimeout(usdSyncTimerRef.current);

    usdSyncTimerRef.current = setTimeout(() => {
      lastSyncedUsdRef.current = { usd, rate };
      syncUsdPriceToVariants(usdArsPrice);
    }, 500);

    return () => {
      if (usdSyncTimerRef.current) clearTimeout(usdSyncTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usdRate, priceUsd, usdArsPrice, isUsdPricing, producto?.id, loading]);

  if (loading) {
    return (
      <div className="h-full min-h-screen flex flex-col items-center justify-center p-10 bg-slate-50/50">
        <RefreshCcw className="animate-spin text-[#51A7FD] mb-4" size={36} />
        <p className="font-black uppercase tracking-[0.3em] text-slate-400 text-[10px]">Cargando...</p>
      </div>
    );
  }

  if (!producto) return null;

  return (
    <div className="imp-page imp-container animate-nova-in">
      
      {/* HEADER: Título y Acciones */}
      <div className="imp-header mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <Package size={18} className="text-[#3FF2AA]" />
          </div>

          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase truncate">
              Producto
            </h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Ficha técnica y operación (Solo lectura)
            </p>
          </div>
        </div>

        <div className="imp-header-actions">
          <button
            onClick={() => go('productos')}
            className="px-4 py-2.5 rounded-2xl bg-white border border-slate-200 shadow-sm text-slate-500 hover:text-slate-800 transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
          >
            <ArrowLeft size={14} /> Volver
          </button>

          {deletionPolicy.canDelete && (
            <button
              onClick={abrirPanelEliminar}
              disabled={deleteBusy || Boolean(deleteImpact)}
              className="px-4 py-2.5 rounded-2xl bg-white border border-red-200 shadow-sm text-[#F05A57] hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
            >
              <Trash2 size={14} /> Eliminar
            </button>
          )}

          <button
            onClick={() => go('producto-nuevo', producto.id)}
            className="px-5 py-2.5 rounded-2xl bg-[#3FF2AA] text-[#0B2A36] shadow-lg hover:shadow-xl transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
          >
            <Edit3 size={16} /> Editar Producto
          </button>
        </div>
      </div>

      {/* CONFIRMACIÓN DE ELIMINACIÓN (embebida, sin modal) */}
      {deleteImpact && (() => {
        const { counts, hasHistory, totalUnits, sitesWithStock } = deleteImpact;
        const bloqueado = hasHistory && !deletionPolicy.canDeleteWithMovements;
        const nombreProducto = producto?.name || '';
        const nombreOk =
          !hasHistory ||
          deleteConfirmName.trim().toLowerCase() === nombreProducto.trim().toLowerCase();

        const ataduras = [
          counts.sales > 0 && `${counts.sales} ${counts.sales === 1 ? 'línea de venta' : 'líneas de venta'}`,
          counts.stockMoves > 0 && `${counts.stockMoves} ${counts.stockMoves === 1 ? 'movimiento de stock' : 'movimientos de stock'}`,
          counts.production > 0 && `${counts.production} ${counts.production === 1 ? 'orden de producción' : 'órdenes de producción'}`,
          counts.comboUses > 0 && `${counts.comboUses} ${counts.comboUses === 1 ? 'combo' : 'combos'}`,
          counts.recipeUses > 0 && `${counts.recipeUses} ${counts.recipeUses === 1 ? 'receta que lo usa como ingrediente' : 'recetas que lo usan como ingrediente'}`,
        ].filter(Boolean);

        return (
          <div className={`mb-4 rounded-2xl border p-4 ${bloqueado ? 'bg-slate-50 border-slate-200' : 'bg-red-50/60 border-red-200'}`}>
            <div className="flex items-start gap-3">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 shadow-sm ${bloqueado ? 'bg-white text-slate-400' : 'bg-[#F05A57] text-white'}`}>
                <AlertTriangle size={16} />
              </div>

              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-black uppercase tracking-tight text-slate-800">
                  {bloqueado
                    ? 'Este producto no se puede eliminar'
                    : hasHistory
                    ? `Eliminar "${nombreProducto}" y ajustar su stock a 0`
                    : `Eliminar "${nombreProducto}" definitivamente`}
                </p>

                {ataduras.length > 0 && (
                  <p className="mt-1 text-[10px] font-bold text-slate-500">
                    Tiene {ataduras.join(', ')}.
                  </p>
                )}

                {!bloqueado && hasHistory && (
                  <p className="mt-1.5 text-[10px] font-bold text-slate-600 leading-relaxed">
                    {sitesWithStock > 0
                      ? `Se ajustará el stock a 0 en ${sitesWithStock} ${sitesWithStock === 1 ? 'sucursal' : 'sucursales'} (${totalUnits} ${Math.abs(totalUnits) === 1 ? 'unidad' : 'unidades'} en total), dejando el movimiento registrado en el kardex. `
                      : 'No tiene stock para ajustar. '}
                    El producto sale del sistema, pero las ventas, el kardex y los reportes históricos se conservan.
                  </p>
                )}

                {!bloqueado && !hasHistory && (
                  <p className="mt-1.5 text-[10px] font-bold text-slate-600 leading-relaxed">
                    No tiene ventas, movimientos de stock, producción, combos ni recetas asociadas.
                    Se borra de forma definitiva junto con sus variantes, costos y niveles de
                    stock. Esta acción no se puede deshacer.
                  </p>
                )}

                {bloqueado && (
                  <p className="mt-1.5 text-[10px] font-bold text-slate-600 leading-relaxed">
                    Para eliminar productos con movimientos hay que habilitar esa opción en
                    Configuración → Mi empresa. La habilita soporte. Mientras tanto, podés
                    desactivarlo desde el listado de productos.
                  </p>
                )}

                {!bloqueado && hasHistory && (
                  <div className="mt-3">
                    <label className="text-[9px] font-black uppercase tracking-widest text-slate-500">
                      Escribí el nombre del producto para confirmar
                    </label>
                    <input
                      type="text"
                      value={deleteConfirmName}
                      onChange={(e) => setDeleteConfirmName(e.target.value)}
                      placeholder={nombreProducto}
                      autoComplete="off"
                      className="mt-1 w-full max-w-sm px-3 py-2 rounded-xl border border-slate-200 bg-white text-[11px] font-bold text-slate-700 outline-none focus:border-[#F05A57]"
                    />
                  </div>
                )}

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => { setDeleteImpact(null); setDeleteConfirmName(''); }}
                    disabled={deleteBusy}
                    className="px-4 py-2 rounded-xl bg-white border border-slate-200 shadow-sm text-slate-600 hover:text-slate-900 disabled:opacity-50 transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
                  >
                    <X size={13} /> {bloqueado ? 'Cerrar' : 'Cancelar'}
                  </button>

                  {!bloqueado && (
                    <button
                      type="button"
                      onClick={confirmarEliminar}
                      disabled={deleteBusy || !nombreOk}
                      className="px-5 py-2 rounded-xl bg-[#F05A57] text-white shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
                    >
                      {deleteBusy ? <RefreshCcw size={13} className="animate-spin" /> : <Trash2 size={13} />}
                      {hasHistory ? 'Ajustar a 0 y eliminar' : 'Eliminar definitivamente'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* KPI: Cards superiores de métricas */}
      <div className="imp-kpis mb-3">
        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            {/* KPI ETIQUETA: Costo por unidad */}
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Costo por unidad</p>
            <FlaskConical size={16} className={receta ? "text-emerald-500" : "text-slate-300"} />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{fmtMoney(sharedCost)}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
             {receta ? 'Gestionado por Ingeniería' : 'Costo manual'}
          </p>
        </div>

        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Precio venta</p>
            <Tag size={16} className="text-slate-300" />
          </div>
          <p className={`mt-1.5 text-2xl font-black tracking-tight ${precioVenta > 0 ? 'text-slate-800' : 'text-rose-700'}`}>
            {precioVenta > 0 ? fmtMoney(precioVenta) : 'SIN PRECIO'}
          </p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Precio final</p>
        </div>

        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Margen real</p>
            <TrendingUp size={16} className="text-slate-300" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{kpi.margen}%</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Rentabilidad</p>
        </div>

        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Stock total</p>
            <Box size={16} className="text-slate-300" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">
            {Number(kpi.stockTotal || 0).toLocaleString('es-AR')}
          </p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Todos los sitios</p>
        </div>
      </div>

      {/* CONTENIDO PRINCIPAL: Grillas y Tarjetas */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Izquierda */}
        <div className="lg:col-span-2 space-y-3">
          {/* Identidad */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-[#0B2A36] px-4 py-2.5 border-b border-white/10 flex items-center gap-2">
              <Box size={16} className="text-[#3FF2AA]" />
              <span className="text-[10px] font-black text-white uppercase tracking-widest">
                Identidad del producto
              </span>
            </div>

            <div className="p-4 flex flex-col md:flex-row gap-4">
              <div className="w-24 h-24 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center overflow-hidden shrink-0 shadow-inner">
                {resolveImageUrl(producto.image_url)
                  ? <img src={resolveImageUrl(producto.image_url)} alt="Producto" className="w-full h-full object-cover" />
                  : <Box size={26} className="text-slate-200" />
                }
              </div>

              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                <div className="md:col-span-2">
                  <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest block mb-1">
                    Nombre
                  </label>
                  <p className="text-[12px] font-black text-slate-800 uppercase tracking-tight">
                    {producto.name}
                  </p>
                </div>

                <div>
                  <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest block mb-1">
                    Categoría
                  </label>
                  <span className="inline-flex text-[10px] font-black uppercase tracking-widest text-slate-600 bg-slate-50 px-3 py-1 rounded-xl border border-slate-100">
                    {producto.product_categories?.name || 'Sin categoría'}
                  </span>
                </div>

                <div>
                  <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest block mb-1">
                    Marca
                  </label>
                  <div className="flex items-center gap-2">
                    <Award size={14} className="text-slate-300" />
                    <span className="inline-flex text-[10px] font-black uppercase tracking-widest text-slate-600">
                      {producto.brand || 'No especificada'}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest block mb-1">
                    Tipo
                  </label>
                  <span className="inline-flex text-[10px] font-black uppercase tracking-widest text-slate-600 bg-slate-50 px-3 py-1 rounded-xl border border-slate-100">
                    {producto.product_type || 'Producto Terminado'}
                  </span>
                </div>

                <div>
                  <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest block mb-1">
                    Código de barras
                  </label>
                  <div className="flex items-center gap-2">
                    <Barcode size={14} className="text-slate-300" />
                    <p className="text-[10px] font-black text-slate-700 tracking-widest">
                      {producto.barcode || 'S/R'}
                    </p>
                  </div>
                </div>


                {/* DESCRIPCIÓN COMPLETA */}
                {producto.description && (
                  <div className="md:col-span-2 mt-2 pt-3 border-t border-slate-100">
                     <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest block mb-1 flex items-center gap-1">
                      <AlignLeft size={10} /> Descripción
                    </label>
                    <p className="text-[10px] text-slate-600 leading-relaxed font-medium">
                      {producto.description}
                    </p>
                  </div>
                )}

                {/* Ayuda usuario final (sin tecnicismos DB) */}
                {!producto.description && (
                  <div className="md:col-span-2">
                    <div className="mt-1 flex items-start gap-2 p-3 rounded-2xl bg-slate-50 border border-slate-100">
                      <Info size={14} className="text-slate-300 mt-0.5" />
                      <p className="text-[9px] font-bold text-slate-500 leading-relaxed">
                        Consejo: definí <span className="font-black">precio</span> y <span className="font-black">stock mínimo</span> para mantener el catálogo listo para vender.
                      </p>
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>

          {/* Stock por sitio */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-[#0B2A36] px-4 py-2.5 border-b border-white/10 flex items-center gap-2">
              <MapPin size={16} className="text-[#3FF2AA]" />
              <span className="text-[10px] font-black text-white uppercase tracking-widest">
                Stock por sitio
              </span>
            </div>

            <div className="p-4">
              {(Array.isArray(producto.stock_levels) && producto.stock_levels.length > 0) ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {producto.stock_levels.map((lvl) => {
                    const qty = Number(lvl?.quantity || 0);
                    const min = Number(producto?.min_stock || 0);
                    const ok = min <= 0 ? (qty > 0) : (qty > min);

                    return (
                      <div
                        key={lvl.id}
                        className="p-3.5 bg-slate-50/50 border border-slate-100 rounded-2xl flex justify-between items-center hover:border-blue-200 transition-all"
                      >
                        <div className="min-w-0">
                          <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1 truncate">
                            {lvl.sitios?.nombre || 'Sin sitio'}
                          </p>
                          <p className="text-sm font-black text-slate-800 tracking-tighter">
                            {qty.toLocaleString('es-AR')}
                            <span className="text-[8px] text-slate-400 uppercase">
                              {' '}{uom}
                            </span>
                          </p>
                        </div>
                        <div className={`w-2 h-2 rounded-full shadow-sm ${ok ? 'bg-emerald-400' : 'bg-amber-400'}`} />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="py-10 text-center text-slate-300 text-[10px] font-black uppercase tracking-[0.3em]">
                  Sin stock por sitio
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Derecha: Configuración & Rentabilidad (Solo Lectura) */}
        <div className="space-y-3">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-[#0B2A36] px-4 py-2.5 border-b border-white/10 flex items-center gap-2">
              <Settings size={16} className="text-[#3FF2AA]" />
              <span className="text-[10px] font-black text-white uppercase tracking-widest">
                Configuración
              </span>
            </div>

            <div className="p-4 space-y-3">
              {/* Stock mínimo */}
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Stock mínimo</p>
                  <p className="text-[10px] font-bold text-slate-500">Umbral para alertas operativas</p>
                </div>
                <span className="inline-flex px-3 py-1 rounded-xl text-[9px] font-black uppercase tracking-widest border bg-slate-50 text-slate-600 border-slate-100">
                  {Number(producto.min_stock || 0).toLocaleString('es-AR')} {uom}
                </span>
              </div>

              {/* Unidad */}
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Unidad</p>
                  <p className="text-[10px] font-bold text-slate-500">Formato de operación</p>
                </div>
                <span className="inline-flex px-3 py-1 rounded-xl text-[9px] font-black uppercase tracking-widest border bg-slate-50 text-slate-600 border-slate-100">
                  {producto.units_of_measure?.name || 'S/D'}
                </span>
              </div>

              {/* Auto price update */}
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Actualización automatica de precio</p>
                  <p className="text-[10px] font-bold text-slate-500">Si está activado, el sistema puede recalcular</p>
                </div>

                {/* <button
                  type="button"
                  onClick={handleToggleAutoPrice}
                  className={`relative inline-flex items-center h-7 w-14 rounded-full border transition-all active:scale-[0.99] ${
                    producto.auto_update_price ? 'bg-[#E9FFF6] border-[#3FF2AA]/40' : 'bg-slate-100 border-slate-200'
                  }`}
                  title="Cambiar"
                >
                  <span
                    className={`absolute top-1 left-1 h-5 w-5 rounded-full shadow-sm transition-all ${
                      producto.auto_update_price ? 'translate-x-7 bg-[#3FF2AA]' : 'translate-x-0 bg-slate-300'
                    }`}
                  />
                </button> 
                */}
                <div className={`relative inline-flex items-center h-7 w-14 rounded-full border transition-all cursor-not-allowed opacity-80 ${
                    producto.auto_update_price ? 'bg-[#E9FFF6] border-[#3FF2AA]/40' : 'bg-slate-100 border-slate-200'
                  }`}
                  title="Para editar, ve a Modificar Producto"
                >
                  <span className={`absolute top-1 left-1 h-5 w-5 rounded-full shadow-sm transition-all ${
                      producto.auto_update_price ? 'translate-x-7 bg-[#3FF2AA]' : 'translate-x-0 bg-slate-300'
                    }`}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Rentabilidad compartida */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-[#0B2A36] px-4 py-2.5 border-b border-white/10 flex items-center gap-2">
              <DollarSign size={16} className="text-[#3FF2AA]" />
              <span className="text-[10px] font-black text-white uppercase tracking-widest">
                Precio y margen
              </span>
            </div>

            <div className="p-4 space-y-3">
              {/* IVA y precio USD */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                <div className="md:col-span-4">
                  <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">
                    IVA (%)
                  </label>
                  {/* <input
                    type="number"
                    value={ivaPercent}
                    onChange={(e) => setIvaPercent(e.target.value)}
                    onBlur={handleSaveIva}
                    className="mt-1 w-full bg-white border border-slate-200 px-3 py-2 rounded-xl text-[11px] font-black outline-none focus:border-[#51A7FD] shadow-sm"
                  /> 
                  */}
                  <div className="mt-1 w-full bg-slate-50 border border-slate-100 px-3 py-2 rounded-xl text-[11px] font-black text-slate-500 cursor-not-allowed">
                    {ivaPercent}%
                  </div>
                  <p className="mt-1 text-[9px] font-bold text-slate-400">
                    Se usa en facturación.
                  </p>
                </div>

                <div className="md:col-span-4">
                  <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">
                    Precio USD (opcional)
                  </label>
                  {/* <input
                    type="number"
                    value={priceUsd}
                    onChange={(e) => setPriceUsd(e.target.value)}
                    onBlur={handleSaveUsdPrice}
                    className="mt-1 w-full bg-white border border-slate-200 px-3 py-2 rounded-xl text-[11px] font-black outline-none focus:border-[#51A7FD] shadow-sm"
                    placeholder="0"
                  /> 
                  */}
                  <div className="mt-1 w-full bg-slate-50 border border-slate-100 px-3 py-2 rounded-xl text-[11px] font-black text-slate-500 cursor-not-allowed">
                    {priceUsd > 0 ? `US$ ${priceUsd}` : 'No'}
                  </div>
                  <p className="mt-1 text-[9px] font-bold text-slate-400">
                    Ligado a cotización.
                  </p>
                </div>

                <div className="md:col-span-4">
                  <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">
                    Cotización USD (ARS)
                  </label>
                  <div className="mt-1 w-full bg-slate-50 border border-slate-100 px-3 py-2 rounded-xl text-[11px] font-black text-slate-700">
                    {usdRate ? Number(usdRate).toLocaleString('es-AR') : 'No configurada'}
                  </div>
                  {isUsdPricing && !usdRate ? (
                    <div className="mt-1 text-[9px] font-bold text-amber-600">
                      Falta cargar la cotización en Empresa → Datos.
                    </div>
                  ) : null}
                  {usdArsPrice != null ? (
                    <div className="mt-1 text-[9px] font-bold text-slate-500">
                      Precio ARS calculado: <span className="font-black">{fmtMoney(usdArsPrice)}</span>
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3">
                {/* COSTO UNITARIO */}
                <div>
                  <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex justify-between">
                     <span>Costo por unidad</span>
                     {receta && <span className="text-[#51A7FD] flex items-center gap-1 cursor-pointer hover:underline" onClick={() => go('recetas')}><FlaskConical size={10} /> Ver receta</span>}
                  </label>
                  <div className="relative mt-1">
                      {/* <input
                          type="number"
                          value={sharedCost}
                          onChange={handleCostChange}
                          onBlur={() => saveSharedEconomics({ cost: sharedCost })}
                          className={`w-full border px-3 py-2 rounded-xl text-[11px] font-black outline-none shadow-sm transition-colors
                              ${receta ? 'bg-slate-50 border-slate-200 text-slate-600 focus:bg-white focus:border-[#51A7FD]' : 'bg-white border-slate-200 focus:border-[#51A7FD]'}`}
                      /> 
                      */}
                      <div className="w-full bg-slate-50 border border-slate-100 px-3 py-2 rounded-xl text-[11px] font-black text-slate-700 cursor-not-allowed">
                         $ {sharedCost}
                      </div>
                      {receta && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" title="Sugerido por receta">
                              <Lock size={12} />
                          </div>
                      )}
                  </div>
                  {receta && <p className="mt-1 text-[9px] font-bold text-slate-400 flex items-center gap-1"><Info size={10} /> Este costo se actualiza desde el módulo Recetas.</p>}
                </div>

                <div>
                  <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">
                    Precio de venta
                  </label>
                  {/* <input
                    type="number"
                    value={sharedPrice}
                    onChange={(e) => setSharedPrice(e.target.value)}
                    onBlur={() => saveSharedEconomics({ price: sharedPrice })}
                    disabled={isUsdPricing && Boolean(usdRate)}
                    className="mt-1 w-full bg-white border border-slate-200 px-3 py-2 rounded-xl text-[11px] font-black outline-none focus:border-[#51A7FD] shadow-sm disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed"
                  /> 
                  */}
                  <div className="mt-1 w-full bg-slate-50 border border-slate-100 px-3 py-2 rounded-xl text-[11px] font-black text-slate-700 cursor-not-allowed">
                     $ {sharedPrice}
                  </div>
                  {isUsdPricing && usdRate ? (
                    <p className="text-[9px] font-bold text-slate-400 -mt-2">
                      Precio ARS bloqueado por precio USD + cotización.
                    </p>
                  ) : null}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">
                      Margen objetivo (%)
                    </label>
                    {/* <input
                      type="number"
                      value={targetMargin}
                      onChange={handleMarginChange}
                      onBlur={() => saveSharedEconomics({ target_margin: targetMargin })}
                      className="mt-1 w-full bg-white border border-slate-200 px-3 py-2 rounded-xl text-[11px] font-black outline-none focus:border-[#51A7FD] shadow-sm"
                    /> 
                    */}
                    <div className="mt-1 w-full bg-slate-50 border border-slate-100 px-3 py-2 rounded-xl text-[11px] font-black text-slate-700 cursor-not-allowed">
                       {targetMargin}%
                    </div>
                  </div>

                  <div>
                    <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">
                      Precio sugerido
                    </label>
                    <div className="mt-1 w-full bg-slate-50 border border-slate-100 px-3 py-2 rounded-xl text-[11px] font-black text-slate-700">
                      {fmtMoney(suggestedPrice)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Talles y colores (matriz + stock por talle). Solo si la org lo activó
          o el producto ya maneja talles. */}
      {producto && (
        <VariantesMatrizEditor product={producto} onChanged={fetchDatosCompletos} />
      )}

      {/* BLOQUE FINAL: Opciones / Variantes (texto libre, legacy) */}
      {orgEnableVariants && !isVariantTracked(producto) && (showVariantsBlock || showOptionsBlock) && (
        <div className="mt-3 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="bg-[#0B2A36] px-4 py-2.5 border-b border-white/10 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Layers size={16} className="text-[#3FF2AA]" />
              <span className="text-[10px] font-black text-white uppercase tracking-widest">
                {showVariantsBlock ? 'Variantes' : 'Opciones del producto'}
              </span>
              <span className="text-[9px] font-black uppercase tracking-widest text-white/40">
                {variants.length}
              </span>
            </div>

            {/* BOTÓN AGREGAR COMENTADO (NO SE PUEDE DESDE DETALLE) */}
            {/* <button
              type="button"
              onClick={() => setShowAddVariant(true)}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl font-black uppercase tracking-widest text-[10px] shadow-sm border transition active:scale-[0.99]"
              style={{ background: GREEN, color: GREEN_TEXT, borderColor: 'rgba(63,242,170,0.35)' }}
              title="Agregar opción"
            >
              <Plus size={14} />
              Agregar
            </button> 
            */}
          </div>

          <div className="p-4 space-y-3">
            {/* Nueva opción bloqueada por defecto en detalle 
            {showAddVariant ? (
               ...
            ) : null} 
            */}

            {/* Tarjetas por opción */}
            <div className="grid grid-cols-1 gap-3">
              {variants.map((v, idx) => {
                const vName = getAttr(v, 'variant_name', '');
                const vDesc = getAttr(v, 'variant_description', '');

                const label = vName ? `${producto.name} — ${vName}` : `${producto.name} — Opción ${idx + 1}`;

                return (
                  <div
                    key={v.id}
                    className="rounded-2xl border border-slate-200 bg-white shadow-sm p-3 transition"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[9px] font-black uppercase tracking-widest text-slate-500">
                          {showVariantsBlock ? 'Variante' : 'Opción'} {idx + 1}
                        </p>
                        <p className="mt-1 text-[11px] font-black text-slate-900 truncate" title={label}>
                          {label}
                        </p>
                      </div>

                      {/* BOTÓN ACTIVAR/DESACTIVAR COMENTADO */}
                      {/* <button
                        type="button"
                        onClick={() => {
                          const next = !vActive;
                          if (!next) {
                            const actives = variants.filter(x => getAttr(x, 'variant_active', true) !== false);
                            if (actives.length <= 1) {
                              toast.error('Debe quedar al menos una opción activa.');
                              return;
                            }
                          }
                          updateVariantMeta(v.id, { attributes: buildVariantAttributes(v, { variant_active: next }) });
                        }}
                        className="px-3 py-1.5 rounded-xl font-black uppercase tracking-widest text-[10px] border shadow-sm transition active:scale-[0.99]"
                        style={{
                          background: vActive ? '#EFF6FF' : '#FEE2E2',
                          color: vActive ? '#0B2A36' : '#7F1D1D',
                          borderColor: vActive ? 'rgba(81,167,253,0.35)' : 'rgba(244,63,94,0.35)'
                        }}
                        title={vActive ? 'Desactivar opción' : 'Activar opción'}
                      >
                        {vActive ? 'Activa' : 'Inactiva'}
                      </button> 
                      */}
                    </div>

                    <div className="mt-3 grid grid-cols-1 md:grid-cols-12 gap-3">
                      <div className="md:col-span-4">
                        <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">
                          Nombre de opción
                        </label>
                        {/* <input
                          defaultValue={vName}
                          onBlur={(e) => updateVariantMeta(v.id, { attributes: buildVariantAttributes(v, { variant_name: e.target.value }) })}
                          className="mt-1 w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-[11px] font-bold outline-none focus:border-[#51A7FD] shadow-sm"
                          placeholder="Ej: Verde L"
                        /> 
                        */}
                        <div className="mt-1 w-full bg-slate-50 border border-slate-100 rounded-xl px-3 py-2 text-[11px] font-bold text-slate-600 truncate">{vName || '-'}</div>
                      </div>

                      <div className="md:col-span-4">
                        <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">
                          SKU (opcional)
                        </label>
                        {/* <input
                          defaultValue={v.sku || ''}
                          onBlur={(e) => updateVariantMeta(v.id, { sku: e.target.value || null })}
                          className="mt-1 w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-[11px] font-bold outline-none focus:border-[#51A7FD] shadow-sm"
                          placeholder="Ej: REM-VER-L"
                        /> 
                        */}
                        <div className="mt-1 w-full bg-slate-50 border border-slate-100 rounded-xl px-3 py-2 text-[11px] font-bold text-slate-600 truncate">{v.sku || '-'}</div>
                      </div>

                      <div className="md:col-span-4">
                        <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">
                          Código/Barras (opcional)
                        </label>
                        {/* <input
                          defaultValue={v.barcode || ''}
                          onBlur={(e) => updateVariantMeta(v.id, { barcode: e.target.value || null })}
                          className="mt-1 w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-[11px] font-bold outline-none focus:border-[#51A7FD] shadow-sm"
                          placeholder="Ej: 779..."
                        /> 
                        */}
                        <div className="mt-1 w-full bg-slate-50 border border-slate-100 rounded-xl px-3 py-2 text-[11px] font-bold text-slate-600 truncate">{v.barcode || '-'}</div>
                      </div>

                      <div className="md:col-span-12">
                        <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">
                          Descripción de la opción
                        </label>
                        {/* <textarea
                          defaultValue={vDesc}
                          onBlur={(e) => updateVariantMeta(v.id, { attributes: buildVariantAttributes(v, { variant_description: e.target.value }) })}
                          className="mt-1 w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-[11px] font-bold outline-none focus:border-[#51A7FD] shadow-sm min-h-[72px] resize-none"
                          placeholder="Detalle para mostrar/usar en la operación (color, talle, etc.)"
                        /> 
                        */}
                        <div className="mt-1 w-full bg-slate-50 border border-slate-100 rounded-xl px-3 py-2 text-[11px] font-bold text-slate-600 min-h-[50px] whitespace-pre-wrap">{vDesc || '-'}</div>
                      </div>
                    </div>

                    {/* BOTÓN ELIMINAR COMENTADO */}
                    {/* <div className="mt-3 flex items-center justify-end">
                      <button
                        type="button"
                        onClick={() => deleteVariant(v.id)}
                        className="px-3 py-1.5 rounded-xl border border-slate-200 bg-white text-slate-700 font-black uppercase tracking-widest text-[10px] hover:bg-slate-50"
                        title="Eliminar opción"
                      >
                        Eliminar
                      </button>
                    </div> 
                    */}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <style>{`
        .animate-nova-in { animation: novaFadeIn 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes novaFadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
};

export default ProductoDetalle;
